import { Suspense, lazy, useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Home from "@/pages/Home";
import CookieConsent from "@/components/CookieConsent";
import DemoBookingDrawer from "@/components/DemoBookingDrawer";
import { initConsent } from "@/consent/consentManager";

const JobManagement = lazy(() => import("@/pages/JobManagement"));
const SchedulingDispatch = lazy(() => import("@/pages/SchedulingDispatch"));
const CustomerManagement = lazy(() => import("@/pages/CustomerManagement"));
const TasksWorkflows = lazy(() => import("@/pages/TasksWorkflows"));
const MultiCompanyManagement = lazy(() => import("@/pages/MultiCompanyManagement"));
const AiOperationsAssistant = lazy(() => import("@/pages/AiOperationsAssistant"));
const AiInsights = lazy(() => import("@/pages/AiInsights"));
const IntelligentAutomation = lazy(() => import("@/pages/IntelligentAutomation"));
const SmartNotifications = lazy(() => import("@/pages/SmartNotifications"));
const AnalyticsReporting = lazy(() => import("@/pages/AnalyticsReporting"));
const CrmEnquiries = lazy(() => import("@/pages/CrmEnquiries"));
const SalesPipeline = lazy(() => import("@/pages/SalesPipeline"));
const QuotesInvoicing = lazy(() => import("@/pages/QuotesInvoicing"));
const RetailEpos = lazy(() => import("@/pages/RetailEpos"));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const FieldApp = lazy(() => import("@/pages/FieldApp"));
const DigitalForms = lazy(() => import("@/pages/DigitalForms"));
const StockInventory = lazy(() => import("@/pages/StockInventory"));
const PickingLoading = lazy(() => import("@/pages/PickingLoading"));
const ReturnsDispatch = lazy(() => import("@/pages/ReturnsDispatch"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const MissedEnquiries = lazy(() => import("@/pages/MissedEnquiries"));
const Contact = lazy(() => import("@/pages/Contact"));
const SpeedUpScheduling = lazy(() => import("@/pages/SpeedUpScheduling"));
const ReduceAdminWorkload = lazy(() => import("@/pages/ReduceAdminWorkload"));
const GetPaidFaster = lazy(() => import("@/pages/GetPaidFaster"));
const ImproveStockControl = lazy(() => import("@/pages/ImproveStockControl"));
const SalesAdmin = lazy(() => import("@/pages/SalesAdmin"));
const OperationsManager = lazy(() => import("@/pages/OperationsManager"));
const FieldEngineers = lazy(() => import("@/pages/FieldEngineers"));
const WarehouseTeams = lazy(() => import("@/pages/WarehouseTeams"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const FinanceTeams = lazy(() => import("@/pages/FinanceTeams"));
const CrmEnquiriesSolution = lazy(() => import("@/pages/CrmEnquiriesSolution"));
const FieldServices = lazy(() => import("@/pages/FieldServices"));
const InstallationMaintenance = lazy(() => import("@/pages/InstallationMaintenance"));
const RetailTrade = lazy(() => import("@/pages/RetailTrade"));
const MultiSite = lazy(() => import("@/pages/MultiSite"));
const HotTubWellness = lazy(() => import("@/pages/HotTubWellness"));
const About = lazy(() => import("@/pages/About"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const AiInsightsPage = lazy(() => import("@/pages/AiInsightsPage"));
const KnowledgeCentre = lazy(() => import("@/pages/KnowledgeCentre"));
const KnowledgeArticle = lazy(() => import("@/pages/KnowledgeArticle"));
const Careers = lazy(() => import("@/pages/Careers"));
const Guides = lazy(() => import("@/pages/Guides"));
const Updates = lazy(() => import("@/pages/Updates"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Admin = lazy(() => import("@/pages/Admin"));
const Launch = lazy(() => import("@/pages/Launch"));
const AiMarketingAutomation = lazy(() => import("@/pages/AiMarketingAutomation"));
const EmailCampaigns = lazy(() => import("@/pages/EmailCampaigns"));
const CustomerSegmentation = lazy(() => import("@/pages/CustomerSegmentation"));
const CustomerJourneyAutomation = lazy(() => import("@/pages/CustomerJourneyAutomation"));
const CustomerRevenueIntelligence = lazy(() => import("@/pages/CustomerRevenueIntelligence"));
const FinanceManagement = lazy(() => import("@/pages/FinanceManagement"));
const ManagementPnl = lazy(() => import("@/pages/ManagementPnl"));
const BillsExpenses = lazy(() => import("@/pages/BillsExpenses"));
const EcommerceOrderManagement = lazy(() => import("@/pages/EcommerceOrderManagement"));
const SubscriptionsRecurringRevenue = lazy(() => import("@/pages/SubscriptionsRecurringRevenue"));
const HealthSafetyCompliance = lazy(() => import("@/pages/HealthSafetyCompliance"));
const HrStaffManagement = lazy(() => import("@/pages/HrStaffManagement"));
const ImproveCustomerRetention = lazy(() => import("@/pages/ImproveCustomerRetention"));
const RecoverLostSalesOpportunities = lazy(() => import("@/pages/RecoverLostSalesOpportunities"));
const UnderstandBusinessProfitability = lazy(() => import("@/pages/UnderstandBusinessProfitability"));
const AutomateCustomerFollowUps = lazy(() => import("@/pages/AutomateCustomerFollowUps"));
const ReduceMarketingAdministration = lazy(() => import("@/pages/ReduceMarketingAdministration"));
const ConnectOnlineOfflineSales = lazy(() => import("@/pages/ConnectOnlineOfflineSales"));

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

export default function App() {
    useEffect(() => {
        initConsent();
        const lenis = new Lenis({ smoothWheel: true });
        let raf = 0;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/platform/job-management" element={<JobManagement />} />
                    <Route path="/platform/scheduling-dispatch" element={<SchedulingDispatch />} />
                    <Route path="/platform/customer-management" element={<CustomerManagement />} />
                    <Route path="/platform/tasks-workflows" element={<TasksWorkflows />} />
                    <Route path="/platform/multi-company-management" element={<MultiCompanyManagement />} />
                    <Route path="/platform/ai-operations-assistant" element={<AiOperationsAssistant />} />
                    <Route path="/platform/ai-insights" element={<AiInsights />} />
                    <Route path="/platform/intelligent-automation" element={<IntelligentAutomation />} />
                    <Route path="/platform/smart-notifications" element={<SmartNotifications />} />
                    <Route path="/platform/analytics-reporting" element={<AnalyticsReporting />} />
                    <Route path="/platform/crm-enquiries" element={<CrmEnquiries />} />
                    <Route path="/platform/sales-pipeline" element={<SalesPipeline />} />
                    <Route path="/platform/quotes-invoicing" element={<QuotesInvoicing />} />
                    <Route path="/platform/retail-epos" element={<RetailEpos />} />
                    <Route path="/platform/customer-portal" element={<CustomerPortal />} />
                    <Route path="/platform/field-app" element={<FieldApp />} />
                    <Route path="/platform/digital-forms" element={<DigitalForms />} />
                    <Route path="/platform/stock-inventory" element={<StockInventory />} />
                    <Route path="/platform/picking-loading" element={<PickingLoading />} />
                    <Route path="/platform/returns-dispatch" element={<ReturnsDispatch />} />
                    <Route path="/platform/ai-marketing-automation" element={<AiMarketingAutomation />} />
                    <Route path="/platform/email-campaigns" element={<EmailCampaigns />} />
                    <Route path="/platform/customer-segmentation" element={<CustomerSegmentation />} />
                    <Route path="/platform/customer-journey-automation" element={<CustomerJourneyAutomation />} />
                    <Route path="/platform/customer-revenue-intelligence" element={<CustomerRevenueIntelligence />} />
                    <Route path="/platform/finance-management" element={<FinanceManagement />} />
                    <Route path="/platform/management-pnl" element={<ManagementPnl />} />
                    <Route path="/platform/bills-expenses" element={<BillsExpenses />} />
                    <Route path="/platform/ecommerce-order-management" element={<EcommerceOrderManagement />} />
                    <Route path="/platform/subscriptions-recurring-revenue" element={<SubscriptionsRecurringRevenue />} />
                    <Route path="/platform/health-safety-compliance" element={<HealthSafetyCompliance />} />
                    <Route path="/platform/hr-staff-management" element={<HrStaffManagement />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/cookie-policy/" element={<CookiePolicy />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/terms/" element={<Terms />} />
                    <Route path="/solutions/stop-missed-enquiries" element={<MissedEnquiries />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/contact/" element={<Contact />} />
                    <Route path="/solutions/speed-up-scheduling" element={<SpeedUpScheduling />} />
                    <Route path="/solutions/reduce-admin-workload" element={<ReduceAdminWorkload />} />
                    <Route path="/solutions/get-paid-faster" element={<GetPaidFaster />} />
                    <Route path="/solutions/improve-stock-control" element={<ImproveStockControl />} />
                    <Route path="/solutions/sales-admin" element={<SalesAdmin />} />
                    <Route path="/solutions/operations-manager" element={<OperationsManager />} />
                    <Route path="/solutions/field-engineers" element={<FieldEngineers />} />
                    <Route path="/solutions/warehouse-teams" element={<WarehouseTeams />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/solutions/finance-teams" element={<FinanceTeams />} />
                    <Route path="/solutions/improve-customer-retention" element={<ImproveCustomerRetention />} />
                    <Route path="/solutions/recover-lost-sales-opportunities" element={<RecoverLostSalesOpportunities />} />
                    <Route path="/solutions/understand-business-profitability" element={<UnderstandBusinessProfitability />} />
                    <Route path="/solutions/automate-customer-follow-ups" element={<AutomateCustomerFollowUps />} />
                    <Route path="/solutions/reduce-marketing-administration" element={<ReduceMarketingAdministration />} />
                    <Route path="/solutions/connect-online-offline-sales" element={<ConnectOnlineOfflineSales />} />
                    <Route path="/solutions/crm-enquiries" element={<CrmEnquiriesSolution />} />
                    <Route path="/industries/field-services" element={<FieldServices />} />
                    <Route path="/industries/installation-maintenance" element={<InstallationMaintenance />} />
                    <Route path="/industries/retail-trade" element={<RetailTrade />} />
                    <Route path="/industries/multi-site" element={<MultiSite />} />
                    <Route path="/industries/hot-tub-wellness" element={<HotTubWellness />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/integrations" element={<Integrations />} />
                    <Route path="/ai-insights" element={<AiInsightsPage />} />
                    <Route path="/knowledge-centre" element={<KnowledgeCentre />} />
                    <Route path="/knowledge-centre/:slug" element={<KnowledgeArticle />} />
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/guides" element={<Guides />} />
                    <Route path="/updates" element={<Updates />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    {/* Hidden utility pages (not linked, noindex): owner admin + one-page launch version */}
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/" element={<Admin />} />
                    <Route path="/launch" element={<Launch />} />
                    <Route path="/launch/" element={<Launch />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </Suspense>
            <CookieConsent />
            <DemoBookingDrawer />
        </BrowserRouter>
    );
}
